import { CreateMovieRequest, CreateMovieResponse, MediaLink, Movie } from '@/types/model';
import { apiClient, QueryParams, PaginatedResponse } from './apiClient';
import type { PlaybackLanguageParams, PlaybackUrlResponse } from './tvShowService';

export type MovieListItem = Pick<Movie, 'id' | 'title' | 'year' | 'genres' | 'categoryNames' | 'views' | 'active' | 'updatedDate'>;

type MoviePlaybackLink = Pick<MediaLink, 'url' | 'quality' | 'active'> & {
  audioLanguages?: string[];
  subtitleLanguages?: string[];
};

export interface MoviesQueryParams extends QueryParams {
  categoryId?: string;
  genre?: string;
  year?: number;
  classification?: string;
  active?: boolean;
  adultContent?: boolean;
  kidsContent?: boolean;
}

export const movieService = {
  async getMovieList(params: MoviesQueryParams = {}): Promise<PaginatedResponse<MovieListItem>> {
    return apiClient.getPaginated<MovieListItem>('/movies', { ...params, _view: 'list' });
  },
  /**
   * Get movies with pagination, search, and filtering
   */
  async getMovies(params: MoviesQueryParams = {}): Promise<PaginatedResponse<Movie>> {
    return apiClient.getPaginated<Movie>('/movies', params);
  },

  /**
   * Get a single movie by ID
   */
  async getMovieById(id: string): Promise<Movie> {
    return apiClient.getById<Movie>('/movies', id);
  },

  async getMoviePlaybackUrl(
    movieId: string,
    params: PlaybackLanguageParams = {},
  ): Promise<PlaybackUrlResponse> {
    return apiClient.get<PlaybackUrlResponse>(
      `/app/content/movies/${movieId}/play-url`,
      params,
    );
  },

  async getLinkPlaybackUrl(
    link: MoviePlaybackLink,
    params: PlaybackLanguageParams = {},
  ): Promise<PlaybackUrlResponse> {
    return apiClient.post<PlaybackUrlResponse>('/movies/links/play-url', {
      url: link.url,
      quality: link.quality,
      active: link.active,
      audioLanguages: link.audioLanguages ?? [],
      subtitleLanguages: link.subtitleLanguages ?? [],
    }, { params });
  },

  /**
   * Create a new movie
   */
  async createMovie(movieData: CreateMovieRequest): Promise<CreateMovieResponse> {
    return apiClient.create<CreateMovieRequest, CreateMovieResponse>('/movies', movieData);
  },

  /**
   * Update a movie
   */
  async updateMovie(id: string, movieData: Partial<CreateMovieRequest>): Promise<CreateMovieResponse> {
    return apiClient.update<Partial<CreateMovieRequest>, CreateMovieResponse>('/movies', id, movieData);
  },

  /**
   * Delete a movie
   */
  async deleteMovie(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/movies', id);
  },

  /**
   * Toggle movie active status
   */
  async toggleMovieStatus(id: string, active: boolean): Promise<CreateMovieResponse> {
    return apiClient.toggleStatus<CreateMovieResponse>('/movies', id, active);
  },

  async promoteMovie(id: string): Promise<CreateMovieResponse> {
    return apiClient.post<CreateMovieResponse>(`/movies/${id}/promote`);
  },
};
